import { Inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { CONSTANTS } from 'src/app/config/constants.module';
import { MessageService } from 'src/app/core/service/message.service';
import { MainMapService } from 'src/app/shared/service/main-map.service';
import { AppParameterHandler } from '../../model/app-parameter-handler.model';
import { ZoomToPointParameters } from './model/zoom-to-point-params.model';
import { EsriGraphic, EsriPoint } from '../../../../../config/esri.module';
import { Arbeitsbereich } from '../../../../../config/model/arbeitsbereich-config.model';
import { ArbeitsbereichService } from '../../../../../shared/service/arbeitsbereich.service';
import { SelectionService } from '../../../../../shared/service/selection.service';

@Injectable({
  providedIn: 'root',
})
export class ZoomToPointService implements AppParameterHandler {
  name: string;

  constructor(
    @Inject(CONSTANTS) private CONST: any,
    private messageService: MessageService,
    private mainMapService: MainMapService,
    private arbeitsbereichService: ArbeitsbereichService,
    private selectionService: SelectionService
  ) {
    this.name = this.CONST.APP_URL_PARAMS.FUNCTIONS.ZOOM_TO_POINT;
  }

  /**
   * goes to a specific point, in specific (optional) scale
   */
  parameterFunction(params: Params) {
    const p: ZoomToPointParameters = this.zoomToPointParams(params);
    if (this.paramsAreValid(p)) {
      this.messageService.success('NOTIFICATIONS.SUCCESS.ZOOM_TO_POINT_PARAMETRS_SUCCESS');
      this.zoomToPoint(p.easting1, p.northing1, p.scale, p.workspace);
    }
  }

  /**
   * converts the params to ZoomToPointParams & returns it
   * @param params the params to convert
   */
  private zoomToPointParams(params: Params): ZoomToPointParameters {
    const p = new ZoomToPointParameters();
    p.easting1 = parseInt(params[this.CONST.APP_URL_PARAMS.PARAMETER_NAMES.EASTING1]);
    p.northing1 = parseInt(params[this.CONST.APP_URL_PARAMS.PARAMETER_NAMES.NORTHING1]);
    p.scale = parseInt(params[this.CONST.APP_URL_PARAMS.PARAMETER_NAMES.SCALE]);
    p.workspace = params[this.CONST.APP_URL_PARAMS.PARAMETER_NAMES.WORKSPACE];
    return p;
  }

  /**
   * check if params ar valid
   * @param p the params to check
   */
  private paramsAreValid(p: ZoomToPointParameters): boolean {
    return this.isInSwitzerland(p) && this.coordinatesAreSet(p);
  }

  /**
   * check if e & n have values
   * @param p the parameters to check
   */
  private coordinatesAreSet(p: ZoomToPointParameters): boolean {
    let valid = true;
    if (!p.easting1 || !p.northing1) {
      this.messageService.error('NOTIFICATIONS.ERROR.APP_PARAMETERS.INVALID_ZOOM_TO_POINT_PARAMS');
      valid = false;
    }
    return valid;
  }

  /**
   * Check if easting and northing is in switzerland
   * @param p the parameters to check
   */
  private isInSwitzerland(p: ZoomToPointParameters) {
    let valid = true;
    if (p.easting1 < 2480000 || p.easting1 > 2840000 || p.northing1 < 1070000 || p.northing1 > 1300000) {
      this.messageService.error('NOTIFICATIONS.ERROR.APP_PARAMETERS.EASTING1_NOT_IN_CH');
      valid = false;
    }
    return valid;
  }

  /***
   * Zoom in on a specific point in the map
   * @param eastings1
   * @param northings1
   * @param scale
   * @param arbeitsBereichId
   */
  private zoomToPoint(eastings1: number, northings1: number, scale: number, arbeitsBereichId?: string) {
    try {
      if (arbeitsBereichId) {
        const arbeitsBereich: Arbeitsbereich = this.arbeitsbereichService.arbeitsBereiche.filter(
          f => f.id === arbeitsBereichId
        )[0];
        this.arbeitsbereichService.setSelected(arbeitsBereich);
      }

      const zoomPoint = new EsriPoint({
        x: eastings1,
        y: northings1,
        spatialReference: this.mainMapService.mapView.extent.spatialReference,
      });

      const newGrphic = new EsriGraphic({
        geometry: zoomPoint,
      });

      this.selectionService.goToFeature(newGrphic, { animate: false }, scale);
      this.selectionService.setSearchMarker(zoomPoint);
    } catch (error) {
      throw Error('ERROR: (zoomToPoint) An error occurred trying to Zoom to an Point: ' + error);
    }
  }
}
